import { getTestAttributes, noop } from '@chillibean/component-library';
import { PropTypes } from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { Key } from 'ts-key-enum';

import TextFieldWithLabel from '../../../../../../../primitives/form/TextField/TextField';
import OtherOptionBox from '../../../../../../Forms/components/presentational/OtherOptionBox';
import { validateAField } from '../../../../utils/formTemplateValidation';

export function AddARadioOption(props) {
  const {
    options,
    handleChange = noop,
    handleOtherOption,
    handleOtherFieldLabel,
    fieldPosition,
    validateAllFields = false,
    readOnly,
    hasOtherEnabled = false,
    otherFieldLabel = 'Other',
  } = props;

  const [validation, setValidation] = useState(false);
  const optionsRef = useRef({});
  const optionValues = options.slice(0, -1);
  const optionInput = options[options.length - 1];

  const handleValidation = () => {
    return validateAField([{ fieldType: 'minimumOption', fieldValue: options }]);
  };

  const handleOnChange = (event, index) => {
    const values = options.map((option, i) => {
      return i === index ? event.target.value : option;
    });
    const valuesWithoutBlanks = values.filter((v) => v);
    handleChange(valuesWithoutBlanks, 'option');
  };

  const handleOnBlur = () => {
    setValidation(handleValidation());
  };

  const handleOnClick = () => {
    setValidation(false);
  };

  const onKeyUp = (event) => {
    if (Key.Enter === event.key) {
      handleOnClick();
    }
  };

  useEffect(() => {
    if (validateAllFields) {
      setValidation(handleValidation());
    }
  }, [validateAllFields]);

  useEffect(() => {
    const index = optionValues.length;
    if (index > 0) {
      optionsRef.current[index - 1].focus();
    }
  }, [optionValues.length]);

  const fieldNameOption = (index) => {
    return index !== 1 && index === options.length && !hasOtherEnabled ? (
      <>
        Option {index} or
        <span
          className="radio-other-link"
          onClick={() => handleOtherOption(true)}
          onKeyUp={onKeyUp}
          role="button"
          tabIndex={0}
          {...getTestAttributes('add-other-option-button')}
        >
          add &quot;other&quot;
        </span>
      </>
    ) : (
      <>Option {index}</>
    );
  };

  return (
    <div className="addAField__options">
      {optionValues.map((value, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="form__group-wrapper form__group-wrapper--half-on-desktop" key={`option-${index}`}>
          <div className="form__group">
            <TextFieldWithLabel
              showError={validation.isError}
              errorMessage={validation.errorMessage}
              fieldID={`${fieldPosition}_options_${index + 1}`}
              fieldIdentifier={`field-${fieldPosition}-option-${index + 1}`}
              fieldName={fieldNameOption(index + 1)}
              fieldRequired={index === 0}
              value={value}
              handleChange={(event) => handleOnChange(event, index)}
              handleClick={handleOnClick}
              handleBlur={handleOnBlur}
              disabled={readOnly}
              autoComplete="off"
              inputRef={(el) => {
                optionsRef.current[index] = el;
              }}
            />
          </div>
        </div>
      ))}
      {hasOtherEnabled && (
        <OtherOptionBox
          handleOtherOptionClick={handleOtherOption}
          handleOtherFieldLabel={handleOtherFieldLabel}
          otherFieldLabel={otherFieldLabel}
        />
      )}
      <div className="form__group-wrapper form__group-wrapper--half-on-desktop">
        <div className="form__group">
          <TextFieldWithLabel
            showError={validation.isError}
            errorMessage={validation.errorMessage}
            fieldID={`${fieldPosition}_options_${options.length}`}
            fieldIdentifier={`field-${fieldPosition}-option-${options.length}`}
            fieldName={fieldNameOption(options.length)}
            value={optionInput}
            handleChange={(event) => handleOnChange(event, options.length - 1)}
            handleClick={handleOnClick}
            handleBlur={handleOnBlur}
            disabled={readOnly}
            autoComplete="off"
          />
        </div>
      </div>
    </div>
  );
}

AddARadioOption.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleChange: PropTypes.func,
  handleOtherOption: PropTypes.func.isRequired,
  handleOtherFieldLabel: PropTypes.func.isRequired,
  fieldPosition: PropTypes.number.isRequired,
  validateAllFields: PropTypes.bool,
  readOnly: PropTypes.bool.isRequired,
  hasOtherEnabled: PropTypes.bool,
  otherFieldLabel: PropTypes.string,
};

export default AddARadioOption;
